// Tracks whether the POS is "busy" so an over-the-air bundle swap never
// interrupts a transaction. Any part of the app can flag activity by key
// (e.g. a non-empty cart, an in-flight payment, an open till session); the OTA
// updater only applies a downloaded bundle when nothing is busy.
//
// This is deliberately a tiny module-level registry rather than React state:
// the OTA scheduler runs outside the component tree and just needs a cheap,
// synchronous isIdle() read.

const busyKeys = new Set();
const listeners = new Set();

const notify = () => {
  const idle = busyKeys.size === 0;
  listeners.forEach((listener) => {
    try {
      listener(idle);
    } catch (err) {
      // A misbehaving listener must not break activity tracking.
      // eslint-disable-next-line no-console
      console.warn('[otaActivity] listener failed', err);
    }
  });
};

// Mark a named activity busy. Idempotent — the same key marked twice is one busy.
export const markBusy = (key) => {
  if (!key) return;
  if (!busyKeys.has(key)) {
    busyKeys.add(key);
    notify();
  }
};

// Clear a named activity. Idempotent.
export const markIdle = (key) => {
  if (busyKeys.delete(key)) {
    notify();
  }
};

// Convenience: mark busy when `active` is truthy, idle otherwise. Handy inside
// a React effect: setActivity('checkout-cart', cart.length > 0).
export const setActivity = (key, active) => {
  if (active) {
    markBusy(key);
  } else {
    markIdle(key);
  }
};

// True when no activity is in progress and it is safe to apply an update.
export const isIdle = () => busyKeys.size === 0;

// Current busy keys — for diagnostics/logging only.
export const busyReasons = () => Array.from(busyKeys);

// Subscribe to idle/busy transitions; returns an unsubscribe function.
export const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Test-only reset.
export const __resetActivity = () => {
  busyKeys.clear();
  listeners.clear();
};
